import NotifierBorder from '@/components/ui/notifier/notifier-border';
import NotifierWrapper from '@/components/ui/notifier/notifier-wrapper';
import { ENUM__component_type } from '@/enums/ENUM__component_type';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { HOOK__notifier } from '@/hooks/HOOK__notifier';
import { use_slice_collections, use_slice_grid2d, use_slice_ui } from '@/hooks/store/use_slices';
import { ButtonComponentActionEmpty } from '@/pages/_base/buttons/ButtonComponentActionEmpty';
import { ButtonWorkspaceChildEntityDelete } from '@/pages/_base/buttons/ButtonWorkspaceChildEntityDelete';
import { ButtonWorkspaceChildEntityShare } from '@/pages/_base/buttons/ButtonWorkspaceChildEntityShare';
import { DRAG_AND_DROP__grid2d_internal } from '@/pages/_base/grid2d/DRAG_AND_DROP__grid2d_internal';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { set_collection_current } from '@/utils/utils_client_workspace_child_entity_viewers';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { DTO__collection } from '@shared/dto/collection/DTO__collection';
import { DTO__grid2d } from '@shared/dto/grid2d/DTO__grid2d';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { СButtonUpdateVisibility } from '../buttons/СButtonUpdateVisibility';
import { ButtonGrid2dCollectionUpdateName } from './buttons/ButtonGrid2dCollectionUpdateName';

export interface Props {
  item: DTO__collection;
  set_is_edit_name: (value: boolean) => void;
}

export function C_LIST_ITEM__collection({ item, set_is_edit_name: setIsEditName }: Props) {
  /*
   * FIELDS
   */
  const dispatch = use_dispatch();

  const { t } = useTranslation(ENUM__i18n_namespace.GRID2D);

  const { grids2d_map } = use_slice_grid2d(['grids2d_map']);
  const { collection_current } = use_slice_collections(['collection_current']);
  const { global_is_desktop, global_is_mobile } = use_slice_ui(['global_is_desktop', 'global_is_mobile']);

  const [is_mouse_over, set_is_mouse_over] = useState(false);
  const [is_button_delete_clicked, set_is_button_delete_clicked] = useState(false);

  const component_id = `${ENUM__component_type.PAGE_COLLECTION_ITEM_LIST}_${item._id}`;
  const notifier = HOOK__notifier(component_id);

  const item_title_component_id = `${ENUM__component_type.PAGE_COLLECTION_ITEM_LIST_TITLE}_${item._id}`;
  const notifier_item_title = HOOK__notifier(item_title_component_id);

  const grid2d = useMemo(() => get_grid2d(), [item]);

  /*
   * EFFECTS
   */
  use_effect(() => {
    if (is_button_delete_clicked) return;

    set_is_mouse_over(false);
  }, [is_button_delete_clicked]);

  /*
   * EVENTS
   */

  function onClick() {
    set_collection_current(item._id);
  }
  function onMouseLeaveItem() {
    if (is_button_delete_clicked) return;

    set_is_mouse_over(false);
  }

  /*
   * METHODS
   */

  function is_selected() {
    return collection_current._id == item._id;
  }

  function get_grid2d() {
    if (!grids2d_map.has(item._id)) return DTO__grid2d.CREATE_STUB();

    return grids2d_map.get_or_assert(item._id);
  }

  /*
   * RENDERS
   */
  function render_button_change_visibility() {
    if (!is_mouse_over && global_is_desktop) return null;

    return <СButtonUpdateVisibility item={item} isSelected={is_selected()} />;
  }

  function render_button_edit_name() {
    if (!is_mouse_over && global_is_desktop) return null;

    return <ButtonGrid2dCollectionUpdateName setIsEditName={setIsEditName} isSelected={is_selected()} />;
  }

  function render_button_share() {
    let out = null;

    if (is_mouse_over || item.is_shared || global_is_mobile) {
      out = (
        <ButtonWorkspaceChildEntityShare
          isSelected={is_selected() || is_mouse_over}
          item={item}
          required_permission={ENUM__workspace_entity_feature.ENTITY_COLLECTION_SHARE}
        />
      );
    }

    return out;
  }

  function render_button_delete() {
    if (!is_mouse_over && global_is_desktop) return null;

    return (
      <ButtonWorkspaceChildEntityDelete
        setIsButtonDeleteClicked={set_is_button_delete_clicked}
        entity={item}
        required_permission={ENUM__workspace_entity_feature.ENTITY_COLLECTION_DELETE}
        isSelected={is_selected()}
      />
    );
  }

  function render_control_buttons() {
    let out = <></>;

    if (!is_mouse_over && item.is_shared) {
      out = (
        <>
          <ButtonComponentActionEmpty />
          {render_button_share()}
          <ButtonComponentActionEmpty />
        </>
      );
    } else {
      out = (
        <>
          {render_button_edit_name()}
          {render_button_change_visibility()}
          {render_button_share()}
          {render_button_delete()}
        </>
      );
    }

    return <div className="flex flex-row items-center justify-center gap-1">{out}</div>;
  }

  return (
    <div
      className={`list-item-wrapper relative px-2
        ${item.is_hidden ? 'opacity-50' : ''}
        `}
      onMouseEnter={() => set_is_mouse_over(true)}
      onMouseLeave={() => onMouseLeaveItem()}
      {...{ onClick }}
    >
      <div className={`relative list-item-inner hover-animation pl-3 pr-1 ${is_mouse_over || is_selected() ? 'list-item-selected' : ''}`}>
        <NotifierBorder {...{ notifier }} />
        <NotifierWrapper className="text-left m-0 truncate whitespace-nowrap w-full" notifier={notifier_item_title}>
          {item.name}
        </NotifierWrapper>
        {render_control_buttons()}
      </div>

      <DRAG_AND_DROP__grid2d_internal {...{ grid2d, entity: item }} />
    </div>
  );
}
